import { useRef, useEffect } from 'react';
import type { MathLockParams, MathLockState } from '../types';

interface Props {
  params: MathLockParams;
  puzzleState: MathLockState;
  onSolve: () => void;
  patchState: (updater: (prev: MathLockState) => MathLockState) => void;
}

export function MathLock({ params, puzzleState, onSolve, patchState }: Props) {
  const { digits, answer, clueText } = params;
  const { digits: inputDigits, solved, wrong } = puzzleState;
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Clear wrong flag after animation
  useEffect(() => {
    if (!wrong) return;
    const id = setTimeout(() => {
      patchState(s => ({ ...s, wrong: false, digits: Array(digits).fill('') }));
    }, 800);
    return () => clearTimeout(id);
  }, [wrong]); // eslint-disable-line react-hooks/exhaustive-deps

  function handleChange(index: number, value: string) {
    const digit = value.replace(/\D/g, '').slice(-1);
    patchState(s => {
      const next = [...s.digits];
      next[index] = digit;
      return { ...s, digits: next, wrong: false };
    });
    if (digit && index < digits - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  }

  function handleKeyDown(index: number, e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Backspace' && !inputDigits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    if (e.key === 'Enter') {
      checkAnswer();
    }
  }

  function checkAnswer() {
    if (solved) return;
    const entered = inputDigits.join('');
    if (entered.length < digits) return;
    if (entered === answer) {
      patchState(s => ({ ...s, solved: true }));
      onSolve();
    } else {
      patchState(s => ({ ...s, wrong: true }));
    }
  }

  if (solved) {
    return (
      <p className="puzzle-solved-msg">
        <i className="fa-solid fa-circle-check" aria-hidden="true" /> Lock opened.
      </p>
    );
  }

  return (
    <div className="puzzle-mathlock">
      <p className="puzzle-clue-text">{clueText}</p>
      <p className="puzzle-mathlock__instruction">
        Find the {digits} hidden numbers and enter them in order.
      </p>
      <div className={`puzzle-mathlock__inputs${wrong ? ' puzzle-mathlock__inputs--wrong' : ''}`}>
        {Array.from({ length: digits }, (_, i) => (
          <input
            key={i}
            ref={el => { inputRefs.current[i] = el; }}
            className="puzzle-mathlock__digit"
            type="text"
            inputMode="numeric"
            maxLength={1}
            value={inputDigits[i] ?? ''}
            aria-label={`Digit ${i + 1}`}
            onChange={e => handleChange(i, e.target.value)}
            onKeyDown={e => handleKeyDown(i, e)}
          />
        ))}
      </div>
      {wrong && (
        <p className="puzzle-error-msg">
          <i className="fa-solid fa-triangle-exclamation" aria-hidden="true" /> Wrong code — try again.
        </p>
      )}
      <button
        className="escape-btn escape-btn--primary escape-btn--sm"
        type="button"
        onClick={checkAnswer}
        disabled={inputDigits.join('').length < digits}
      >
        Unlock
      </button>
    </div>
  );
}
